import { useState, useCallback, useEffect } from "react";
import { Auth } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";

import { ENVIRONMENT_CONFIG } from "shared/globals";

const { coreApiUrl } = ENVIRONMENT_CONFIG;

export function useAddresses(iboNumber) {
  const { makeAuthedRequest } = Auth.useContainer();
  const { logTrace, logError } = useLogger({ prefix: "useAddresses" });
  const [addressOptions, setAddressOptions] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [requestId, setRequestId] = useState(0);

  useEffect(() => {
    if (requestId === 0 && addresses != null) return;

    setIsWaiting(true);

    Promise.all([
      // Get addresses for the IBO.
      makeAuthedRequest(coreApiUrl + `/v1/membership/address/${iboNumber}`)
        .then((res) => res.json())
        .then(setAddresses),

      // Get country/state options for address edit/create form.
      makeAuthedRequest(coreApiUrl + `/v1/membership/address`)
        .then((res) => res.json())
        .then(setAddressOptions),
    ])
      .catch(logError)
      .finally(() => {
        setIsWaiting(false);
      });
  }, [requestId]);

  const refreshAddresses = useCallback(() => {
    setRequestId(requestId + 1);
  }, [requestId]);

  const verifyAddress = useCallback((address) => {
    return makeAuthedRequest(`${coreApiUrl}/v1/shipping/address/resolve`, {
      method: "POST",
      body: JSON.stringify({
        address: {
          city: address.city,
          country: address.country,
          line1: address.line1,
          line2: address.line2 || "",
          postalCode: address.postalCode,
          recipient: address.recipient || "",
          state: address.state,
        },
        performGeolocation: true,
        performTaxCodes: true,
        performValidation: true,
        showRecipient: true,
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const json = await res.json();
          throw new Error(json.error);
        }
      })
      .then((data) => {
        return {
          addressId: address.addressId,
          iboAddressId: address.iboAddressId,
          city: data.city,
          country: data.country,
          line1: data.line1,
          line2: data.line2 || "",
          postalCode: data.postalCode,
          recipient: address.recipient || "",
          state: data.state,
          latitude: data.latitude,
          longitude: data.longitude,
        };
      })
      .catch(logError);
  }, []);

  const saveAddress = useCallback(
    async (address) => {
      let path;
      let method;

      if (address.addressId == null) {
        // Create a new address.
        path = `/membership/address/${iboNumber}`;
        method = "POST";
      } else {
        // Update an existing address.
        path = `/membership/address/${iboNumber}/info`;
        method = "PUT";
      }

      return makeAuthedRequest(path, {
        method,
        body: JSON.stringify(convertAddressToUpdateObject(address)),
      })
        .catch(logError)
        .finally(() => {
          logTrace("refreshing addresses after save");
          refreshAddresses();
        });
    },
    [iboNumber, makeAuthedRequest, logError, logTrace, refreshAddresses]
  );

  const deleteAddress = useCallback(
    async (iboAddressId) => {
      return makeAuthedRequest(
        `/membership/address/${iboNumber}/${iboAddressId}`,
        {
          method: "DELETE",
        }
      )
        .catch(logError)
        .finally(() => {
          logTrace("refreshing addresses after delete");
          refreshAddresses();
        });
    },
    [iboNumber, makeAuthedRequest, logError, logTrace, refreshAddresses]
  );

  return {
    addresses,
    addressOptions,
    saveAddress,
    deleteAddress,
    verifyAddress,
    refreshAddresses,
    isWaiting,
  };
}

function convertAddressToUpdateObject(address) {
  return {
    addressId: address.addressId,
    city: address.city,
    country: address.country,
    line1: address.line1,
    line2: address.line2 || "",
    postalCode: address.postalCode,
    recipient: address.recipient || "",
    state: address.state,
    iboAddressId: address.iboAddressId,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}
